import {
  Component,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ChatbotMessage } from 'src/app/interfaces/chatbot-message.interfaces';

/**
 * ChatbotMessageComponent — Renderiza un mensaje individual del chatbot.
 *
 * Distingue visualmente entre mensajes del usuario y del asistente.
 * Muestra un indicador de escritura animado cuando el mensaje del asistente
 * está en streaming y aún no tiene contenido (esperando el primer token).
 * Muestra un cursor parpadeante mientras el contenido se va acumulando.
 *
 * Requisitos: 5.4, 5.5, 5.7, 5.8
 */
@Component({
  selector: 'app-chatbot-message',
  templateUrl: './chatbot-message.component.html',
  styleUrls: ['./chatbot-message.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatbotMessageComponent {

  /**
   * Mensaje a renderizar.
   * Contiene el rol ('user' | 'assistant'), el contenido y la marca de tiempo.
   */
  @Input() message!: ChatbotMessage;

  /**
   * Indica si este mensaje es el que está recibiendo tokens en este momento.
   * Cuando es `true` y el contenido está vacío, se muestra el indicador de
   * escritura (tres puntos animados).
   * Cuando es `true` y hay contenido, se muestra el cursor parpadeante al final.
   */
  @Input() isStreaming: boolean = false;

  // ---------------------------------------------------------------------------
  // Getters de conveniencia para la plantilla
  // ---------------------------------------------------------------------------

  /** Devuelve `true` si el mensaje fue enviado por el usuario. */
  get isUser(): boolean {
    return this.message?.role === 'user';
  }

  /** Devuelve `true` si el mensaje fue generado por el asistente. */
  get isAssistant(): boolean {
    return this.message?.role === 'assistant';
  }

  /**
   * Devuelve `true` cuando se debe mostrar el indicador de escritura
   * (tres puntos animados): el mensaje es del asistente, está en streaming
   * y todavía no tiene contenido (esperando el primer token).
   * Req 5.5
   */
  get showTypingIndicator(): boolean {
    return this.isAssistant && this.isStreaming && !this.message?.content;
  }

  /**
   * Devuelve `true` cuando se debe mostrar el cursor parpadeante al final
   * del texto: el mensaje es del asistente, está en streaming y ya tiene
   * contenido parcial.
   * Req 5.4
   */
  get showStreamingCursor(): boolean {
    return this.isAssistant && this.isStreaming && !!this.message?.content;
  }

  /**
   * Formatea la marca de tiempo del mensaje para mostrarla en la UI.
   * Devuelve una cadena vacía si no hay timestamp.
   */
  get formattedTimestamp(): string {
    if (!this.message?.timestamp) {
      return '';
    }
    try {
      const date = new Date(this.message.timestamp);
      return date.toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }
}
